/**
 * Protocol utilities - now using BD SDK instead of Contentful
 */

import { fetchProtocolMetadata } from "./sdk-protocol-metadata";

// Type for serializable protocol data
export interface SerializableProtocol {
  id: string;
  name: string;
  description: string;
  symbol: string;
  logo: {
    url: string;
    title?: string;
    description?: string;
  };
  createdAt: string;
  updatedAt: string;
}

/**
 * Enum for supported protocol IDs
 */
enum ProtocolId {
  AAVE_V3_ETHEREUM = "1200",
  AAVE_V3_TESTNET_SEPOLIA = "1202",
  // COMPOUND_V3_TESTNET = "1302",
  AAVE_V2_AVALANCHE = "1003",
  AAVE_V3_AVALANCHE = "1203",
  AAVE_V2_POLYGON = "1005",
  AAVE_V3_POLYGON = "1205",
  AAVE_V3_ARBITRUM = "1204",
  AAVE_V3_BASE = "1206",
  // MORPHO_BASE = "1402",
  AAVE_V3_OPTIMISM = "1207",
}

export enum ProtocolKey {
  AAVE_V2 = "aave-v2",
  AAVE_V3 = "aave-v3",
  AAVE_V3_TESTNET = "aave-v3-testnet",
  COMPOUND_V2 = "compound-v2",
  COMPOUND_V3 = "compound-v3",
  MORPHO = "morpho",
}
export interface LendBorrowProject {
  id: string; // Changed from ProtocolId enum to string for SDK compatibility
  key: ProtocolKey;
  name: string;
  chainId: string;
  logoUrl: string;
  isTestnet?: boolean;
}

// TODO: remove this once we have a proper logo in sdk
const aaveLogo =
  "https://images.ctfassets.net/hwqnbx905qdp/1mtpezUbEfFb77N0L066hw/eda58c468a7959398f015d7c9e545d46/Aave_Logo.svg";
// Lending protocol IDs and chain IDs from Expand Network docs
// Chain IDs use CAIP-2 format per https://docs.portalhq.io/resources/chain-id-formatting
// Note: This is internal only - not exported to avoid unused export warnings
const LENDBORROW_PROJECTS: LendBorrowProject[] = [
  {
    id: ProtocolId.AAVE_V3_ETHEREUM,
    key: ProtocolKey.AAVE_V3,
    name: "Aave V3",
    chainId: "eip155:1",
    logoUrl: aaveLogo,
  },
  // Ethereum Testnet Sepolia
  {
    id: ProtocolId.AAVE_V3_TESTNET_SEPOLIA,
    key: ProtocolKey.AAVE_V3_TESTNET,
    name: "Aave V3 Testnet",
    chainId: "eip155:11155111",
    logoUrl: aaveLogo,
    isTestnet: true,
  },
  // {
  //   id: "1302",
  //   name: "Compound V3 Testnet",
  //   chainId: "eip155:11155111",
  // },

  // Avalanche
  {
    id: ProtocolId.AAVE_V2_AVALANCHE,
    key: ProtocolKey.AAVE_V2,
    name: "Aave V2",
    chainId: "eip155:43114",
    logoUrl: aaveLogo,
  },
  {
    id: ProtocolId.AAVE_V3_AVALANCHE,
    key: ProtocolKey.AAVE_V3,
    name: "Aave V3",
    chainId: "eip155:43114",
    logoUrl: aaveLogo,
  },

  // Polygon
  {
    id: ProtocolId.AAVE_V2_POLYGON,
    name: "Aave V2",
    key: ProtocolKey.AAVE_V2,
    chainId: "eip155:137",
    logoUrl: aaveLogo,
  },
  {
    id: ProtocolId.AAVE_V3_POLYGON,
    key: ProtocolKey.AAVE_V3,
    name: "Aave V3",
    chainId: "eip155:137",
    logoUrl: aaveLogo,
  },

  // Arbitrum
  {
    id: ProtocolId.AAVE_V3_ARBITRUM,
    key: ProtocolKey.AAVE_V3,
    name: "Aave V3",
    chainId: "eip155:42161",
    logoUrl: aaveLogo,
  },

  // Base
  {
    id: ProtocolId.AAVE_V3_BASE,
    key: ProtocolKey.AAVE_V3,
    name: "Aave V3",
    chainId: "eip155:8453",
    logoUrl: aaveLogo,
  },
  // {
  //   id: "1402",
  //   name: "Morpho",
  //   chainId: "eip155:8453",
  // },

  // Optimism
  {
    id: ProtocolId.AAVE_V3_OPTIMISM,
    key: ProtocolKey.AAVE_V3,
    name: "Aave V3",
    chainId: "eip155:10",
    logoUrl: aaveLogo,
  },
] as const;

// Legacy Contentful compatibility - TODO: refactor to use BD SDK
export const protocolQueryKeys = {
  all: ["protocols"] as const,
  lists: () => [...protocolQueryKeys.all, "list"] as const,
};

export const protocolQueryConfig = {
  staleTime: 5 * 60 * 1000, // 5 minutes
  refetchInterval: 10 * 60 * 1000, // 10 minutes
};

/**
 * Helper function to transform static LENDBORROW_PROJECTS to the expected format
 * Used as fallback when SDK fetch fails
 */
function transformStaticProtocolsToItems() {
  return {
    items: LENDBORROW_PROJECTS.map((project) => ({
      sys: {
        id: project.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      fields: {
        name: project.name,
        symbol: project.key,
        description: `${project.name} protocol on chain ${project.chainId}`,
        logo: {
          fields: {
            file: {
              url: project.logoUrl,
            },
            title: `${project.name} Logo`,
            description: `Logo for ${project.name}`,
          },
        },
      },
    })),
  };
}

export async function fetchProtocols(): Promise<{
  items: Array<{
    sys: { id: string; createdAt: string; updatedAt: string };
    fields: Record<string, unknown>;
  }>;
}> {
  try {
    // Use unified utility to fetch protocol metadata
    const protocolData = await fetchProtocolMetadata();

    if (!protocolData || protocolData.length === 0) {
      // Return static data as fallback
      return transformStaticProtocolsToItems();
    }

    // Transform protocol metadata to expected format
    const items: Array<{
      sys: { id: string; createdAt: string; updatedAt: string };
      fields: Record<string, unknown>;
    }> = [];

    for (const item of protocolData) {
      const protocolName = item.protocolName || "";
      const protocolLogo = item.protocolLogo || aaveLogo;
      const protocolKey = protocolName.toLowerCase().replace(/\s+/g, "-");

      for (const chain of item.supportedChains) {
        items.push({
          sys: {
            id: chain.protocolId || "",
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          fields: {
            name: protocolName,
            symbol: protocolKey,
            description: `${protocolName} protocol on ${chain.chainName || ""}`,
            logo: {
              fields: {
                file: {
                  url: protocolLogo,
                },
                title: `${protocolName} Logo`,
                description: `Logo for ${protocolName}`,
              },
            },
          },
        });
      }
    }

    return { items };
  } catch (error) {
    console.error("Error fetching protocols from BD SDK:", error);
    // Return static data as fallback
    return transformStaticProtocolsToItems();
  }
}
